// XPPen Star G430S (retail SKU StarG430_B1, Amazon ASIN B01ESM8BHO).
//
// USB identity and report layout come from public driver configs, not from a
// capture of the unit bought for this project:
// - OpenTabletDriver Star G430S.json and Star G430S V2.json
//   (VID 10429 / 0x28BD, PID 2323 / 0x0913, and legacy PID 117 / 0x0075)
// - OpenTabletDriver XP_PenReportParser, XP_PenTabletReport and
//   XP_PenTabletOverflowReport (little-endian X/Y/pressure)
// - DIGImend digimend-kernel-drivers#424 (PID 0x0913 = Star G430S)
// - hawku/TabletDriver#520 (G430S_B, product "4 inch PenTablet",
//   usage page 0xFF0A usage 0x0001, init 02 B0 02)
//
// The product page states 5080 LPI and 8192 pressure levels. 5080 × 4 in =
// 20320 and 5080 × 3 in = 15240, which matches the Star G430S (not V2) limits.
// 8192 levels are reported as 0..8191. Init reports are the OpenTabletDriver
// OutputInitReport values: "ArAE" = 02 B0 04 and "ArAC" = 02 B0 02.
//
// Uncertainty: StarG430_B1 is not a separate USB id in those sources. Units
// that share PID 0x0913 are told apart by input report length (14 bytes =
// 5080 LPI, 12 bytes = V2). The usage page is documented for G430S_B only.

export const XP_PEN_VENDOR_ID = 0x28bd

export type Handedness = 'right' | 'left'

export interface SignatureTabletProfile {
  id: string
  displayName: string
  vendorId: number
  productId: number
  /** Input report length including the report id, as OpenTabletDriver counts it. */
  inputReportLength: number
  outputReportLength: number
  maxX: number
  maxY: number
  maxPressure: number
  widthMm: number
  heightMm: number
  /** Full output report, report id in the first byte. */
  initReport: number[]
  usagePage?: number
  usage?: number
  /** When set, a non-empty product name must contain this text. */
  productNameIncludes?: string
}

export const SIGNATURE_TABLETS: SignatureTabletProfile[] = [
  {
    id: 'xppen-star-g430s',
    displayName: 'XPPen Star G430S',
    vendorId: XP_PEN_VENDOR_ID,
    productId: 0x0913,
    inputReportLength: 14,
    outputReportLength: 14,
    maxX: 20320,
    maxY: 15240,
    maxPressure: 8191,
    widthMm: 101.6,
    heightMm: 76.2,
    initReport: [0x02, 0xb0, 0x04],
    usagePage: 0xff0a,
    usage: 0x0001,
  },
  {
    id: 'xppen-star-g430s-v2',
    displayName: 'XPPen Star G430S V2',
    vendorId: XP_PEN_VENDOR_ID,
    productId: 0x0913,
    inputReportLength: 12,
    outputReportLength: 12,
    maxX: 10160,
    maxY: 7620,
    maxPressure: 8191,
    widthMm: 101.6,
    heightMm: 76.2,
    initReport: [0x02, 0xb0, 0x04],
    usagePage: 0xff0a,
    usage: 0x0001,
  },
  {
    id: 'xppen-star-g430s-legacy',
    displayName: 'XPPen Star G430S',
    vendorId: XP_PEN_VENDOR_ID,
    productId: 0x0075,
    inputReportLength: 8,
    outputReportLength: 8,
    maxX: 20320,
    maxY: 15240,
    maxPressure: 8191,
    widthMm: 101.6,
    heightMm: 76.2,
    initReport: [0x02, 0xb0, 0x02],
    usagePage: 0xff0a,
    usage: 0x0001,
    productNameIncludes: 'G430S',
  },
]

export interface PenReport {
  inRange: boolean
  x: number
  y: number
  /** Raw device pressure, already masked into the 0..8191 range when needed. */
  pressure: number
  eraser: boolean
  tipDown: boolean
}

export interface NormalizedTabletPoint {
  phase: 'down' | 'move' | 'up'
  /** 0..1 across the active area. Already mirrored for handedness. */
  x: number
  y: number
  /** 0..1. Zero on pen-up. */
  pressure: number
  time: number
}

export function clamp(value: number, min: number, max: number): number {
  if (value < min) return min
  if (value > max) return max
  return value
}

export function nameMatches(
  profile: SignatureTabletProfile,
  productName: string | undefined,
): boolean {
  if (!profile.productNameIncludes) return true
  if (!productName) return true
  return productName
    .toUpperCase()
    .includes(profile.productNameIncludes.toUpperCase())
}

export function selectTabletProfile(
  vendorId: number,
  productId: number,
  reportLength?: number,
  productName?: string,
): SignatureTabletProfile | null {
  const candidates = SIGNATURE_TABLETS.filter(
    profile =>
      profile.vendorId === vendorId &&
      profile.productId === productId &&
      nameMatches(profile, productName),
  )
  if (candidates.length === 0) return null
  if (reportLength != null) {
    const exact = candidates.find(
      profile => profile.inputReportLength === reportLength,
    )
    if (exact) return exact
  }
  return (
    candidates.find(profile => profile.id === 'xppen-star-g430s') ||
    candidates[0]
  )
}

export function hidRequestFilters(): Array<{
  vendorId: number
  productId: number
  usagePage?: number
  usage?: number
}> {
  const filters: Array<{
    vendorId: number
    productId: number
    usagePage?: number
    usage?: number
  }> = []
  const seen = new Set<string>()
  SIGNATURE_TABLETS.forEach(tablet => {
    if (tablet.usagePage != null && tablet.usage != null) {
      const usageKey = [
        tablet.vendorId,
        tablet.productId,
        tablet.usagePage,
        tablet.usage,
      ].join(':')
      if (!seen.has(usageKey)) {
        seen.add(usageKey)
        filters.push({
          vendorId: tablet.vendorId,
          productId: tablet.productId,
          usagePage: tablet.usagePage,
          usage: tablet.usage,
        })
      }
    }
    const broadKey = [tablet.vendorId, tablet.productId].join(':')
    if (!seen.has(broadKey)) {
      seen.add(broadKey)
      filters.push({vendorId: tablet.vendorId, productId: tablet.productId})
    }
  })
  return filters
}

function looksLikePenStatus(status: number): boolean {
  return status === 0xc0 || (status & 0x80) !== 0 || (status & 0x10) !== 0
}

export function withReportId(
  reportId: number,
  payload: Uint8Array,
): Uint8Array {
  if (reportId === 0) return payload
  // WebHID omits the report id. If a buffer already starts with it, keep it.
  if (
    payload.length >= 8 &&
    payload[0] === reportId &&
    looksLikePenStatus(payload[1])
  ) {
    return payload
  }
  const report = new Uint8Array(payload.length + 1)
  report[0] = reportId
  report.set(payload, 1)
  return report
}

/**
 * Decode one XP-Pen pen report. Returns null for auxiliary-button reports
 * that are not part of the stroke. Out-of-range reports return inRange false.
 */
export function parseXpPenReport(report: Uint8Array): PenReport | null {
  if (report.length < 8) return null
  const status = report[1]
  if (status === 0xc0) {
    return {
      inRange: false,
      x: 0,
      y: 0,
      pressure: 0,
      eraser: false,
      tipDown: false,
    }
  }
  // Bit 4 marks an auxiliary report in XP_PenReportParser.
  if ((status & 0x10) !== 0) return null

  let x = report[2] | (report[3] << 8)
  let y = report[4] | (report[5] << 8)
  if (report.length >= 12) {
    x |= report[10] << 16
    y |= report[11] << 16
  }
  let pressure = report[6] | (report[7] << 8)
  // Some XP-Pen firmwares leave bit 13 set (OpenTabletDriver PR 4197).
  // The G430S parser normally sees a clean 0..8191 value; mask only the overflow.
  if (pressure > 8191) pressure = pressure & 0x1fff

  const eraser = (status & 0x08) !== 0
  return {
    inRange: true,
    x,
    y,
    pressure,
    eraser,
    tipDown: !eraser && pressure > 0,
  }
}

export function normalizePressure(raw: number, maxPressure: number): number {
  if (maxPressure <= 0) return 0
  return clamp(raw / maxPressure, 0, 1)
}

export function mapTabletToUnitSquare(
  x: number,
  y: number,
  profile: SignatureTabletProfile,
  handedness: Handedness,
): {x: number; y: number} {
  const nx = clamp(profile.maxX === 0 ? 0 : x / profile.maxX, 0, 1)
  const ny = clamp(profile.maxY === 0 ? 0 : y / profile.maxY, 0, 1)
  return {
    x: handedness === 'left' ? 1 - nx : nx,
    y: ny,
  }
}

export function messageForHidError(error: unknown): string | null {
  const name =
    error && typeof error === 'object' && 'name' in error
      ? String((error as {name: unknown}).name)
      : ''
  if (
    name === 'NotAllowedError' ||
    name === 'NotFoundError' ||
    name === 'AbortError'
  ) {
    return null
  }
  if (name === 'SecurityError') {
    return 'La connexion directe à la tablette exige une page en HTTPS.'
  }
  if (name === 'InvalidStateError' || name === 'NetworkError') {
    return 'La tablette est déjà ouverte, souvent par le pilote XPPen. Fermez le pilote, ou signez directement dans le cadre.'
  }
  return 'Impossible d’ouvrir la XPPen Star G430S. Vous pouvez signer dans le cadre avec le pilote installé.'
}
